/**
 * Formulário de uma pessoa da família.
 *
 * Papel: preenche, valida e grava uma pessoa — nome completo e apelido —,
 * servindo tanto à criação quanto à edição.
 *
 * Como usar: quem monta este componente passa `pessoa` para editar ou `null`
 * para criar, e deverá dar a ele uma `key` diferente por pessoa. Sem a `key`, o
 * React reaproveita a mesma instância ao trocar de registro e os campos
 * continuam com o que a pessoa via antes, porque `defaultValues` só é lido na
 * montagem.
 *
 * O apelido é digitado, e não deduzido do nome: não se obtém removendo acentos
 * nem cortando o primeiro nome, e preenchê-lo sozinho acertaria em parte da
 * família e erraria no resto.
 */
import {
  type NovaPessoaFamilia,
  type PessoaFamilia,
  novaPessoaFamiliaSchema,
} from '@concierge/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { type FieldPath, useForm } from 'react-hook-form';

import { aplicarErroDaApi, tradutorPorMapa } from '@/shared/lib/erro-de-formulario';
import { Button } from '@/shared/ui/button';
import { DialogFooter } from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { useSalvarPessoaFamilia } from '../hooks/usar-cadastros-base';
import { PESSOA_EM_BRANCO, type PessoaNoFormulario } from '../types';

interface FormularioPessoaFamiliaProps {
  /** A pessoa a editar, ou `null` para criar uma nova. */
  pessoa: PessoaFamilia | null;
  onSalvo: () => void;
  onCancelar: () => void;
}

/** Os campos que a API pode apontar, cada um no caminho do formulário. */
const CAMPOS: Record<string, FieldPath<PessoaNoFormulario> | undefined> = {
  nomeCompleto: 'nomeCompleto',
  apelido: 'apelido',
};

/**
 * Converte a pessoa vinda da API no formato que o formulário guarda.
 *
 * @param pessoa - A pessoa a editar, ou `null` para começar em branco.
 * @returns Os valores iniciais dos dois campos.
 */
function valoresIniciais(pessoa: PessoaFamilia | null): PessoaNoFormulario {
  if (pessoa === null) return { ...PESSOA_EM_BRANCO };

  return { nomeCompleto: pessoa.nomeCompleto, apelido: pessoa.apelido };
}

/**
 * O formulário de criação e de edição de pessoa da família.
 *
 * @param pessoa - A pessoa a editar, ou `null` para criar.
 * @param onSalvo - Chamado depois de a API confirmar a gravação.
 * @param onCancelar - Chamado quando se desiste, sem gravar nada.
 * @returns O formulário completo, com os botões de ação.
 */
export function FormularioPessoaFamilia({
  pessoa,
  onSalvo,
  onCancelar,
}: FormularioPessoaFamiliaProps) {
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const salvar = useSalvarPessoaFamilia();

  const form = useForm<PessoaNoFormulario, unknown, NovaPessoaFamilia>({
    resolver: zodResolver(novaPessoaFamiliaSchema),
    defaultValues: valoresIniciais(pessoa),
  });

  const { errors } = form.formState;
  const enviando = salvar.isPending;

  /**
   * Grava a pessoa e leva o erro da API para o campo que o causou.
   *
   * O 409 de nome completo repetido chega sem caminho de campo, e por isso
   * aparece acima dos botões.
   */
  const handleSalvar = form.handleSubmit(async (dados) => {
    setErroGeral(null);
    try {
      await salvar.mutateAsync({ id: pessoa?.id ?? null, dados });
      onSalvo();
    } catch (erro) {
      setErroGeral(aplicarErroDaApi(erro, form, tradutorPorMapa(CAMPOS)));
    }
  });

  return (
    <form onSubmit={handleSalvar}>
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-full flex flex-col gap-1.5">
          <Label htmlFor="pessoa-nome-completo">Nome completo</Label>
          <Input
            id="pessoa-nome-completo"
            autoComplete="off"
            disabled={enviando}
            {...form.register('nomeCompleto')}
          />
          {errors.nomeCompleto && (
            <p className="text-sm text-destructive" role="alert">
              {errors.nomeCompleto.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="pessoa-apelido">Apelido</Label>
          <Input
            id="pessoa-apelido"
            autoComplete="off"
            placeholder="Como a pessoa é chamada em casa"
            disabled={enviando}
            {...form.register('apelido')}
          />
          {errors.apelido && (
            <p className="text-sm text-destructive" role="alert">
              {errors.apelido.message}
            </p>
          )}
        </div>

        {erroGeral !== null && (
          <p className="col-span-full text-sm text-destructive" role="alert">
            {erroGeral}
          </p>
        )}
      </div>

      <DialogFooter>
        <Button type="button" variant="ghost" disabled={enviando} onClick={onCancelar}>
          Cancelar
        </Button>
        <Button type="submit" disabled={enviando}>
          {enviando ? 'Salvando…' : 'Salvar pessoa'}
        </Button>
      </DialogFooter>
    </form>
  );
}
